import type { Post } from "@/services/posts.service";
import { parseNormalizedDate } from "@/utils/normalizeIsoString";

export type MediaType = "image" | "video" | "document" | "file" | "unknown";

const isHttpUrl = (value?: string | null): value is string =>
  typeof value === "string" && /^https?:\/\//.test(value);

export interface MediaAttachment {
  url: string;
  type: MediaType;
  is_thumbnail?: boolean;
}

export interface FeedPost {
  id: string;
  stasht_id?: string;
  messageId: string | null;
  title: string;
  description: string;
  caption?: string;
  text?: string;
  full_text?: string;
  vision_description?: string;
  platform: string;
  createdAt: string;
  tags?: string[];
  media: MediaAttachment[];
  primaryMedia?: MediaAttachment;
  sourceUrl?: string;
  canonical_url?: string | null;
  source?: string | { platform?: string; username?: string; type?: string };
  username?: string;
  attachment_type?: string;
  attachment_data?: Record<string, unknown>;
  locations?: string[];
  location_city?: string;
  activity_type?: string;
  overall_message_intent?: string;
  intent?: string;
  category?: string;
  routing_class?: string;
  auto_categories?: Array<{
    routing_class?: string;
    category?: string;
  }>;
  extracted_entities?: Post["extracted_entities"];
  entity_ids?: string[];
  is_raw_only?: boolean;
  is_merged_child?: boolean;
  needs_enrichment?: boolean;
  enrichment_status?: string;
  last_enriched_at?: string;
  unfurl_status?: Post["unfurl_status"];
  pipeline?: Record<string, unknown>;
  ingest?: Post["ingest"];
  content?: Post["content"];
  image_content?: Post["image_content"];
  is_saved?: boolean;
  user_state?: "active" | "removed" | "none";
  user_title?: string;
  can_edit_sensitive?: boolean;
  visibility?: "private" | "public";
}

const normaliseType = (value?: string): MediaType => {
  if (!value) return "unknown";
  const lower = value.toLowerCase();
  if (lower.includes("video")) return "video";
  if (lower.includes("image") || lower.includes("photo") || lower.includes("jpg") || lower.includes("png")) {
    return "image";
  }
  if (lower.includes("pdf") || lower.includes("document")) return "document";
  if (lower.includes("file") || lower.includes("attachment")) return "file";
  return "unknown";
};

const inferTypeFromUrl = (url: string): MediaType => {
  const trimmed = url.split("?")[0]?.toLowerCase() ?? "";
  if (trimmed.endsWith(".mp4") || trimmed.endsWith(".mov") || trimmed.endsWith(".m4v")) return "video";
  if (
    trimmed.endsWith(".jpg") ||
    trimmed.endsWith(".jpeg") ||
    trimmed.endsWith(".png") ||
    trimmed.endsWith(".webp")
  ) {
    return "image";
  }
  if (trimmed.endsWith(".pdf")) return "document";
  return "unknown";
};

export const extractMediaAttachments = (post: Post): MediaAttachment[] => {
  const attachments: MediaAttachment[] = [];

  const addAttachment = (url?: string, typeHint?: string, is_thumbnail?: boolean) => {
    if (!url) return;
    const type = normaliseType(typeHint) !== "unknown" ? normaliseType(typeHint) : inferTypeFromUrl(url);
    attachments.push({ url, type, is_thumbnail });
  };

  const resolveMediaUrl = (item: Record<string, any>) =>
    isHttpUrl(item.cdn_url) ? item.cdn_url : undefined;

  const appendMediaList = (items: Array<Record<string, any>>) => {
    items.forEach(item => {
      if (!item || typeof item !== "object") return;
      addAttachment(resolveMediaUrl(item), item.type ?? item.media_type, item.is_thumbnail);
    });
  };

  const contentMedia = post.content?.media;
  if (Array.isArray(contentMedia) && contentMedia.length > 0) {
    appendMediaList(contentMedia);
  }

  if (attachments.length === 0) {
    const contentMediaUrls = post.content?.media_urls;
    if (Array.isArray(contentMediaUrls)) {
      appendMediaList(contentMediaUrls);
    }
  }

  if (attachments.length === 0) {
    const postMedia = post.media;
    if (Array.isArray(postMedia)) {
      appendMediaList(postMedia as Array<Record<string, any>>);
    }
  }

  if (attachments.length === 0 && isHttpUrl(post.media_url)) {
    addAttachment(post.media_url, post.attachment_type);
  }

  return attachments;
};

export const mapPostToFeed = (post: Post): FeedPost => {
  const id = post.id || post.stasht_id;
  const title = post.user_title || post.title || "";
  const description = post.caption || post.full_text || "";
  const media = extractMediaAttachments(post);
  const createdAt = parseNormalizedDate(post.created_at) ?? null;
  const contentUrl = post.content?.url;
  const sourceUrl = [contentUrl, post.original_url, post.source?.url]
    .find(value => typeof value === "string" && value.length > 0);

  return {
    id,
    stasht_id: post.stasht_id,
    messageId: id,
    title,
    description,
    caption: post.caption,
    text: post.text,
    full_text: post.full_text,
    vision_description: post.vision_description,
    platform: post.platform || post.source?.platform || "unknown",
    createdAt: createdAt ? createdAt.toISOString() : post.created_at,
    tags: post.tags,
    media,
    primaryMedia: media[0],
    sourceUrl,
    canonical_url: post.canonical_url || null,
    source: post.source,
    username: post.username,
    attachment_type: post.attachment_type,
    attachment_data: post.attachment_data,
    locations: post.locations,
    location_city: post.location_city,
    activity_type: post.activity_type,
    overall_message_intent: post.overall_message_intent,
    intent: (post as unknown as Record<string, unknown>).intent as string | undefined,
    category: (post as unknown as Record<string, unknown>).category as string | undefined,
    routing_class: (post as unknown as Record<string, unknown>).routing_class as string | undefined,
    auto_categories: post.auto_categories,
    extracted_entities: post.extracted_entities,
    entity_ids: post.entity_ids,
    is_raw_only: post.is_raw_only,
    is_merged_child: post.is_merged_child,
    needs_enrichment: post.needs_enrichment,
    enrichment_status: post.enrichment_status,
    last_enriched_at: post.last_enriched_at,
    pipeline: post.pipeline as unknown as Record<string, unknown> | undefined,
    ingest: post.ingest,
    content: post.content,
    image_content: post.image_content,
    is_saved: post.is_saved,
    user_state: post.user_state,
    user_title: post.user_title ?? undefined,
    can_edit_sensitive: post.can_edit_sensitive,
    unfurl_status: post.unfurl_status,
    visibility: post.visibility,
  };
};
